"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { JournalCard } from "./JournalCard";

/**
 * Homepage journal strip: a horizontally scroll-snapping row of condensed
 * cards, ending in a "View all articles" card.
 *
 * Native overflow scrolling does the work (touch swipe, trackpad, and
 * keyboard all come free); the arrows are a secondary affordance and only
 * appear when the row actually overflows, so a short row renders static
 * and never looks like a broken carousel. The partially visible next card
 * is the primary affordance: card widths target 3.5 visible at desktop
 * and ~80vw on mobile.
 *
 * Never auto-advances. Arrow scrolling jumps rather than smooth-scrolls
 * under prefers-reduced-motion.
 */

export type JournalRowPost = {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  readingLabel: string;
  image?: string;
  /** Paired with `image`; resolved server-side by getJournalImageAlt. */
  imageAlt?: string;
  /** Overrides the default `/blog/<slug>` target. Draft preview only. */
  href?: string;
  /** Badges the card as unpublished. Draft preview only. */
  draft?: boolean;
};

function Arrow({
  dir,
  onClick,
  disabled,
}: {
  dir: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous articles" : "Next articles"}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/25 text-ink transition-colors hover:border-gold hover:text-gold-deep disabled:pointer-events-none disabled:opacity-30"
    >
      <svg width="16" height="10" viewBox="0 0 22 10" fill="none" aria-hidden="true">
        <path
          d={dir === "next" ? "M0 5h20M16 1l4 4-4 4" : "M22 5H2M6 1L2 5l4 4"}
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="square"
        />
      </svg>
    </button>
  );
}

export function JournalRow({ posts }: { posts: JournalRowPost[] }) {
  const ref = useRef<HTMLUListElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollable(max > 8);
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft >= max - 8);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    // Observe the row AND its first card: the row's own box does not change
    // when the card list does, so a ResizeObserver on the row alone leaves
    // `scrollable` stale in any filtered/re-rendered context. posts.length
    // in the deps covers the re-render; the card observer covers reflow.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const card = el.querySelector("li");
    if (card) ro.observe(card);
    return () => ro.disconnect();
  }, [measure, posts.length]);

  const nudge = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector("li");
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: dir * step, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <div>
      <style href="silk-journal-row" precedence="medium">{`
        .silk-journal-row::-webkit-scrollbar { display: none; }
        .silk-journal-row { scrollbar-width: none; }
      `}</style>

      {/* Arrows: only once the row overflows, and desktop only. Mobile
          swipes natively, where arrows would be redundant chrome. */}
      {scrollable && (
        <div className="mb-6 hidden justify-end gap-2.5 md:flex">
          <Arrow dir="prev" onClick={() => nudge(-1)} disabled={atStart} />
          <Arrow dir="next" onClick={() => nudge(1)} disabled={atEnd} />
        </div>
      )}

      {/* scroll-pl must match the row's own padding: without it, mandatory
          snapping aligns the first card to the scrollport edge and parks the
          row at scrollLeft = padding on load (first card flush to the
          viewport, prev arrow live at rest, no partial-card affordance). */}
      <ul
        ref={ref}
        onScroll={measure}
        className="silk-journal-row -mx-5 flex snap-x snap-mandatory scroll-pl-5 gap-6 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:scroll-pl-8 sm:px-8 lg:-mx-16 lg:scroll-pl-16 lg:px-16"
      >
        {posts.map((post) => (
          <li
            key={post.slug}
            className="w-[80vw] flex-none snap-start sm:w-[340px]"
          >
            <JournalCard {...post} />
          </li>
        ))}

        {/* Final position: the index link, in outline style. */}
        <li className="w-[80vw] flex-none snap-start sm:w-[340px]">
          <Link
            href="/blog"
            className="group flex h-full flex-col justify-center border border-ink/20 px-6 py-10 text-center transition-colors hover:border-gold"
          >
            <span className="font-serif italic text-xl text-ink transition-colors group-hover:text-gold-deep">
              View all articles
            </span>
            <span className="silk-caption mt-3 text-muted">
              The full Journal index
            </span>
            <svg
              className="mx-auto mt-5 text-gold-deep"
              width="22"
              height="10"
              viewBox="0 0 22 10"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M0 5h20M16 1l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="square"
              />
            </svg>
          </Link>
        </li>
      </ul>
    </div>
  );
}
